import type { TFunction } from 'i18next';
import { menuDaAba, modulosWeb, navegacaoDoMenu, type AbaWeb, type ModuloWeb } from './modulos-web';

/**
 * Review r28 visual (O4): nome da tela de uma visão salva (o `tela` da `Tabela`, como "contas.a-receber") pelo mesmo
 * caminho do menu que `docs/CAMINHOS.md` usa (`Menu > Grupo > Aba`), no idioma da pessoa. Configurações > Visões salvas
 * mostrava o identificador cru em fonte mono.
 *
 * Quase todo identificador é `<slug>.<chave da aba>` (com `_` no lugar de `-` em alguns). Os que não seguem a regra, e as
 * listas que moram numa sub-aba interna, ficam aqui; `tests/r28-visual-web.test.ts` confere que todo `tela` do código acha
 * a aba.
 */
const TELA_PARA_ABA: Record<string, { aba: string; sub?: string }> = {
  // CRM no novo desenho (lote 1): as páginas da casca nova sobre o cadastro e a agenda.
  'cadastros.crm-empresas': { aba: 'cadastros.pessoas', sub: 'cadastros:casca.empresas' },
  'cadastros.crm-contatos': { aba: 'cadastros.pessoas', sub: 'cadastros:casca.contatos' },
  'agenda.crm-reunioes': { aba: 'agenda.semana', sub: 'agenda:casca.reunioes' },
  'financeiro.lancamentos': { aba: 'financeiro.entradas-saidas' },
  'financeiro.centros': { aba: 'financeiro.centros-custo' },
  'conversas.respostas': { aba: 'conversas.respostas-rapidas' },
  // Trilha 5b: as respostas por palavra-chave moram na aba Automação.
  'conversas.palavras_chave': { aba: 'conversas.automacoes' },
  'estoque.inventarios': { aba: 'estoque.inventario' },
  'admin.membros': { aba: 'admin.usuarios', sub: 'admin:usuarios.subabas.membros' },
  'treinamento.progresso.pessoas': { aba: 'treinamento.progresso', sub: 'treinamento:progresso.por_pessoas' },
  'treinamento.progresso.cursos': { aba: 'treinamento.progresso', sub: 'treinamento:progresso.por_cursos' },
  // FUN 2026-10-15 (U7): as listas da ficha da automação (abertas pelo Painel).
  'automacoes.execucoes': { aba: 'automacoes.painel', sub: 'automacoes:ficha.abas.execucoes' },
  'automacoes.pendencias': { aba: 'automacoes.painel', sub: 'automacoes:ficha.abas.pendencias' },
  // U14 F5: a lista dos disparos, os destinatários do relatório e os consentimentos moram na aba Broadcast.
  'automacoes.broadcasts': { aba: 'automacoes.broadcast' },
  'automacoes.broadcast.destinatarios': { aba: 'automacoes.broadcast', sub: 'automacoes:broadcast.relatorio.destinatarios' },
  'automacoes.consentimentos': { aba: 'automacoes.broadcast', sub: 'automacoes:broadcast.consentimentos.abrir' },
  'automacoes.resultados': { aba: 'automacoes.painel', sub: 'automacoes:ficha.abas.resultados' },
  // Pacote 2 (Documentos): os modelos moram na aba Contratos, na vista Modelos.
  'documentos.modelos': { aba: 'documentos.contratos', sub: 'documentos:vista.modelos' },
  // Registro de ponto, lote 3: os relógios moram na aba Configuração, no cartão Relógios de ponto.
  'ponto.relogios': { aba: 'ponto.configuracao', sub: 'ponto:relogio.titulo' },
  // CRM no novo desenho (lotes 2 e 3): as páginas Atividades e Comercial do CRM ficam com as abas de sempre do Pipeline.
  'pipeline.crm-atividades': { aba: 'pipeline.atividades' },
  'pipeline.crm-comercial': { aba: 'pipeline.pipeline' },
};

/** Módulo e aba da tela (com a sub-aba, quando a lista mora numa); null quando o identificador não acha uma aba. */
export function abaDaTela(tela: string, lista: ModuloWeb[] = modulosWeb): { modulo: ModuloWeb; aba: AbaWeb; sub: string | null } | null {
  const conhecida = TELA_PARA_ABA[tela];
  const alvo = conhecida?.aba ?? tela;
  const ponto = alvo.indexOf('.');
  if (ponto <= 0) return null;
  const modulo = lista.find((m) => m.slug === alvo.slice(0, ponto));
  const chave = alvo.slice(ponto + 1);
  const aba = modulo?.abas.find((a) => a.chave === chave || a.chave === chave.replace(/_/g, '-'));
  return modulo && aba ? { modulo, aba, sub: conhecida?.sub ?? null } : null;
}

/** Quem vê tudo (todo módulo, toda permissão, a plataforma e o primeiro modo de cada aba): o caminho não depende da pessoa. */
function acessoTotal(lista: ModuloWeb[]) {
  const modos = new Map<string, string>();
  for (const m of lista) for (const a of m.abas) if (a.modo && !modos.has(a.modo.chave)) modos.set(a.modo.chave, a.modo.em[0]!);
  return { instalado: () => true, pode: () => true, modo: (c: string) => modos.get(c), plataforma: true, todasAsEmpresas: true };
}

/** `Menu > Grupo > Aba` (e a sub-aba) da tela no idioma de `t`; o identificador quando não acha a aba (tela que saiu do código). */
export function nomeDaTela(tela: string, t: TFunction, lista: ModuloWeb[] = modulosWeb): string {
  const achada = abaDaTela(tela, lista);
  if (!achada) return tela;
  const { modulo, aba, sub } = achada;
  const menu = menuDaAba(modulo, aba);
  const partes = [t(`menus.${menu}`)];
  const nav = navegacaoDoMenu(menu, acessoTotal(lista), lista);
  if (nav.tipo === 'grupos') {
    const g = nav.grupos.find((x) => x.abas.some((y) => y.slug === modulo.slug && y.aba.chave === aba.chave));
    if (g) partes.push(g.declarado ? t(`navegacao.${menu}.${g.chave}`) : t(`${g.chave}:nome`));
  } else if (nav.tipo === 'modulos' && nav.modulos.length > 1) partes.push(t(`${modulo.slug}:nome`));
  partes.push(t(`${modulo.slug}:abas.${aba.chave}`));
  if (sub) partes.push(t(sub));
  return partes.join(' > ');
}

/**
 * Review r29 visual (C2 e o mesmo caso na Auditoria): o campo `tela` de uma visão salva (`configuracoes.visao`) pelo
 * caminho do menu, como na lista. Vale para o Histórico de Configurações e para Admin > Auditoria; outro campo ou outra
 * entidade devolve null e segue o formatador genérico (`rotuloValor`), que não depende do registro dos módulos.
 */
export function telaDaVisao(entidade: string | undefined, campo: string, v: unknown, t: TFunction): string | null {
  return entidade === 'configuracoes.visao' && campo === 'tela' && typeof v === 'string' ? nomeDaTela(v, t) : null;
}
